/*
 * Copyright (C) 2020-present Borza LLC. All rights reserved.
 */

import { motion } from "framer-motion";
import Link from "next/link";
import { useRouter } from "next/router";

import CollectionKind from "../enums/collection-kind";
import ProductKind from "../enums/product-kind";
import { toRelativePageURL } from "../utils/url";

const RelatedProducts = ({ collection, skipProduct }: { collection: CollectionKind, skipProduct: ProductKind }) => {
  const {
    query: {
      market,
    },
  } = useRouter();

  return (
    <section className="px-2 py-10 sm:px-5">
      <h2 className="font-medium font-serif text-2xl text-center">
        You May Also Like
      </h2>
      {collection === CollectionKind.LipBalm && (
        <div className="flex flex-row flex-wrap items-center justify-around">
          {skipProduct !== ProductKind.PeppermintCremeLipBalm && (
            <div className="flex-initial m-5 text-center">
              <Link
                as={toRelativePageURL("/products/peppermint-creme-lip-balm", market as string)}
                href={{
                  pathname: "/products/peppermint-creme-lip-balm",
                  query: { market },
                }}
              >
                <a>
                  <picture>
                    <source
                      srcSet={require("../public/images/products/peppermint-creme-lip-balm/pack.webp") as string}
                      type="image/webp"
                    />
                    <source
                      srcSet={require("../public/images/products/peppermint-creme-lip-balm/pack.jpg") as string}
                      type="image/jpeg"
                    />
                    <motion.img
                      alt="Lasting Smiles Peppermint Creme Lip Balm"
                      className="rounded"
                      src={require("../public/images/products/peppermint-creme-lip-balm/pack.jpg") as string}
                      style={{ maxWidth: 200 }}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    />
                  </picture>
                  <br />
                  <span className="border-b-2 border-black font-medium inline-block uppercase">
                    Peppermint&nbsp;Creme
                  </span>
                </a>
              </Link>
            </div>
          )}
          {skipProduct !== ProductKind.StrawberryCremeOrganicLipBalm && (
            <div className="flex-initial m-5 text-center">
              <Link
                as={toRelativePageURL("/products/strawberry-creme-organic-lip-balm", market as string)}
                href={{
                  pathname: "/products/strawberry-creme-organic-lip-balm",
                  query: { market },
                }}
              >
                <a>
                  <picture>
                    <source
                      srcSet={require("../public/images/products/strawberry-creme-organic-lip-balm/pack.webp") as string}
                      type="image/webp"
                    />
                    <source
                      srcSet={require("../public/images/products/strawberry-creme-organic-lip-balm/pack.jpg") as string}
                      type="image/jpeg"
                    />
                    <motion.img
                      alt="Lasting Smiles Strawberry Creme Organic Lip Balm"
                      className="rounded"
                      src={require("../public/images/products/strawberry-creme-organic-lip-balm/pack.jpg") as string}
                      style={{ maxWidth: 200 }}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    />
                  </picture>
                  <br />
                  <span className="border-b-2 border-black font-medium inline-block uppercase">
                    Strawberry&nbsp;Creme
                  </span>
                </a>
              </Link>
            </div>
          )}
          {skipProduct !== ProductKind.PassionfruitMangoLipBalm && (
            <div className="flex-initial m-5 text-center">
              <Link
                as={toRelativePageURL("/products/passionfruit-mango-lip-balm", market as string)}
                href={{
                  pathname: "/products/passionfruit-mango-lip-balm",
                  query: { market },
                }}
              >
                <a>
                  <picture>
                    <source
                      srcSet={require("../public/images/products/passionfruit-mango-lip-balm/pack.webp") as string}
                      type="image/webp"
                    />
                    <source
                      srcSet={require("../public/images/products/passionfruit-mango-lip-balm/pack.jpg") as string}
                      type="image/jpeg"
                    />
                    <motion.img
                      alt="Lasting Smiles Passionfruit Mango Lip Balm"
                      className="rounded"
                      src={require("../public/images/products/passionfruit-mango-lip-balm/pack.jpg") as string}
                      style={{ maxWidth: 200 }}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    />
                  </picture>
                  <br />
                  <span className="border-b-2 border-black font-medium inline-block uppercase">
                    Passionfruit&nbsp;Mango
                  </span>
                </a>
              </Link>
            </div>
          )}
          {skipProduct !== ProductKind.PeachBlossomLipBalm && (
            <div className="flex-initial m-5 text-center">
              <Link
                as={toRelativePageURL("/products/peach-blossom-lip-balm", market as string)}
                href={{
                  pathname: "/products/peach-blossom-lip-balm",
                  query: { market },
                }}
              >
                <a>
                  <picture>
                    <source
                      srcSet={require("../public/images/products/peach-blossom-lip-balm/pack.webp") as string}
                      type="image/webp"
                    />
                    <source
                      srcSet={require("../public/images/products/peach-blossom-lip-balm/pack.jpg") as string}
                      type="image/jpeg"
                    />
                    <motion.img
                      alt="Lasting Smiles Peach Blossom Lip Balm"
                      className="rounded"
                      src={require("../public/images/products/peach-blossom-lip-balm/pack.jpg") as string}
                      style={{ maxWidth: 200 }}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    />
                  </picture>
                  <br />
                  <span className="border-b-2 border-black font-medium inline-block uppercase">
                    Peach&nbsp;Blossom
                  </span>
                </a>
              </Link>
            </div>
          )}
          {skipProduct !== ProductKind.VanillaBeanOrganicLipBalm && (
            <div className="flex-initial m-5 text-center">
              <Link
                as={toRelativePageURL("/products/vanilla-bean-organic-lip-balm", market as string)}
                href={{
                  pathname: "/products/vanilla-bean-organic-lip-balm",
                  query: { market },
                }}
              >
                <a>
                  <picture>
                    <source
                      srcSet={require("../public/images/products/vanilla-bean-organic-lip-balm/pack.webp") as string}
                      type="image/webp"
                    />
                    <source
                      srcSet={require("../public/images/products/vanilla-bean-organic-lip-balm/pack.jpg") as string}
                      type="image/jpeg"
                    />
                    <motion.img
                      alt="Lasting Smiles Vanilla Bean Organic Lip Balm"
                      className="rounded"
                      src={require("../public/images/products/vanilla-bean-organic-lip-balm/pack.jpg") as string}
                      style={{ maxWidth: 200 }}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    />
                  </picture>
                  <br />
                  <span className="border-b-2 border-black font-medium inline-block uppercase">
                    Vanilla&nbsp;Bean
                  </span>
                </a>
              </Link>
            </div>
          )}
          {skipProduct !== ProductKind.VeryCherryLipBalm && (
            <div className="flex-initial m-5 text-center">
              <Link
                as={toRelativePageURL("/products/very-cherry-lip-balm", market as string)}
                href={{
                  pathname: "/products/very-cherry-lip-balm",
                  query: { market },
                }}
              >
                <a>
                  <picture>
                    <source
                      srcSet={require("../public/images/products/very-cherry-lip-balm/pack.webp") as string}
                      type="image/webp"
                    />
                    <source
                      srcSet={require("../public/images/products/very-cherry-lip-balm/pack.jpg") as string}
                      type="image/jpeg"
                    />
                    <motion.img
                      alt="Lasting Smiles Very Cherry Lip Balm"
                      className="rounded"
                      src={require("../public/images/products/very-cherry-lip-balm/pack.jpg") as string}
                      style={{ maxWidth: 200 }}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    />
                  </picture>
                  <br />
                  <span className="border-b-2 border-black font-medium inline-block uppercase">
                    Very&nbsp;Cherry
                  </span>
                </a>
              </Link>
            </div>
          )}
          {skipProduct !== ProductKind.VarietyLipBalm && (
            <div className="flex-initial m-5 text-center">
              <Link
                as={toRelativePageURL("/products/variety-lip-balm", market as string)}
                href={{
                  pathname: "/products/variety-lip-balm",
                  query: { market },
                }}
              >
                <a>
                  <picture>
                    <source
                      srcSet={require("../public/images/products/variety-lip-balm/pack.webp") as string}
                      type="image/webp"
                    />
                    <source
                      srcSet={require("../public/images/products/variety-lip-balm/pack.jpg") as string}
                      type="image/jpeg"
                    />
                    <motion.img
                      alt="Lasting Smiles Variety Lip Balm"
                      className="rounded"
                      src={require("../public/images/products/variety-lip-balm/pack.jpg") as string}
                      style={{ maxWidth: 200 }}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    />
                  </picture>
                  <br />
                  <span className="border-b-2 border-black font-medium inline-block uppercase">
                    Variety&nbsp;Collection
                  </span>
                </a>
              </Link>
            </div>
          )}
        </div>
      )}
      {collection === CollectionKind.Candle && (
        <div className="flex flex-row flex-wrap items-center justify-around">
          {skipProduct !== ProductKind.BalanceCandle && (
            <div className="flex-initial m-5 text-center">
              <Link
                as={toRelativePageURL("/products/balance-aromatherapy-cause-candle", market as string)}
                href={{
                  pathname: "/products/balance-aromatherapy-cause-candle",
                  query: { market },
                }}
              >
                <a>
                  <picture>
                    <source
                      srcSet={require("../public/images/products/balance-aromatherapy-cause-candle/box.webp") as string}
                      type="image/webp"
                    />
                    <source
                      srcSet={require("../public/images/products/balance-aromatherapy-cause-candle/box.jpg") as string}
                      type="image/jpeg"
                    />
                    <motion.img
                      alt="Balance - Aromatherapy Cause Candle by Lasting Smiles"
                      className="rounded"
                      src={require("../public/images/products/balance-aromatherapy-cause-candle/box.jpg") as string}
                      style={{ maxWidth: 200 }}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    />
                  </picture>
                  <br />
                  <span className="border-b-2 border-black font-medium inline-block uppercase">
                    Balance
                  </span>
                </a>
              </Link>
            </div>
          )}
          {skipProduct !== ProductKind.BlissCandle && (
            <div className="flex-initial m-5 text-center">
              <Link
                as={toRelativePageURL("/products/bliss-aromatherapy-cause-candle", market as string)}
                href={{
                  pathname: "/products/bliss-aromatherapy-cause-candle",
                  query: { market },
                }}
              >
                <a>
                  <picture>
                    <source
                      srcSet={require("../public/images/products/bliss-aromatherapy-cause-candle/box.webp") as string}
                      type="image/webp"
                    />
                    <source
                      srcSet={require("../public/images/products/bliss-aromatherapy-cause-candle/box.jpg") as string}
                      type="image/jpeg"
                    />
                    <motion.img
                      alt="Bliss - Aromatherapy Cause Candle by Lasting Smiles"
                      className="rounded"
                      src={require("../public/images/products/bliss-aromatherapy-cause-candle/box.jpg") as string}
                      style={{ maxWidth: 200 }}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    />
                  </picture>
                  <br />
                  <span className="border-b-2 border-black font-medium inline-block uppercase">
                    Bliss
                  </span>
                </a>
              </Link>
            </div>
          )}
          {skipProduct !== ProductKind.CalmCandle && (
            <div className="flex-initial m-5 text-center">
              <Link
                as={toRelativePageURL("/products/calm-aromatherapy-cause-candle", market as string)}
                href={{
                  pathname: "/products/calm-aromatherapy-cause-candle",
                  query: { market },
                }}
              >
                <a>
                  <picture>
                    <source
                      srcSet={require("../public/images/products/calm-aromatherapy-cause-candle/box.webp") as string}
                      type="image/webp"
                    />
                    <source
                      srcSet={require("../public/images/products/calm-aromatherapy-cause-candle/box.jpg") as string}
                      type="image/jpeg"
                    />
                    <motion.img
                      alt="Calm - Aromatherapy Cause Candle by Lasting Smiles"
                      className="rounded"
                      src={require("../public/images/products/calm-aromatherapy-cause-candle/box.jpg") as string}
                      style={{ maxWidth: 200 }}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    />
                  </picture>
                  <br />
                  <span className="border-b-2 border-black font-medium inline-block uppercase">
                    Calm
                  </span>
                </a>
              </Link>
            </div>
          )}
          {skipProduct !== ProductKind.FocusCandle && (
            <div className="flex-initial m-5 text-center">
              <Link
                as={toRelativePageURL("/products/focus-aromatherapy-cause-candle", market as string)}
                href={{
                  pathname: "/products/focus-aromatherapy-cause-candle",
                  query: { market },
                }}
              >
                <a>
                  <picture>
                    <source
                      srcSet={require("../public/images/products/focus-aromatherapy-cause-candle/box.webp") as string}
                      type="image/webp"
                    />
                    <source
                      srcSet={require("../public/images/products/focus-aromatherapy-cause-candle/box.jpg") as string}
                      type="image/jpeg"
                    />
                    <motion.img
                      alt="Focus - Aromatherapy Cause Candle by Lasting Smiles"
                      className="rounded"
                      src={require("../public/images/products/focus-aromatherapy-cause-candle/box.jpg") as string}
                      style={{ maxWidth: 200 }}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    />
                  </picture>
                  <br />
                  <span className="border-b-2 border-black font-medium inline-block uppercase">
                    Focus
                  </span>
                </a>
              </Link>
            </div>
          )}
          {skipProduct !== ProductKind.SeaSaltCitrusCandle && (
            <div className="flex-initial m-5 text-center">
              <Link
                as={toRelativePageURL("/products/sea-salt-citrus-aromatherapy-cause-candle", market as string)}
                href={{
                  pathname: "/products/sea-salt-citrus-aromatherapy-cause-candle",
                  query: { market },
                }}
              >
                <a>
                  <picture>
                    <source
                      srcSet={require("../public/images/products/sea-salt-citrus-aromatherapy-cause-candle/box.webp") as string}
                      type="image/webp"
                    />
                    <source
                      srcSet={require("../public/images/products/sea-salt-citrus-aromatherapy-cause-candle/box.jpg") as string}
                      type="image/jpeg"
                    />
                    <motion.img
                      alt="Sea Salt &amp; Citrus - Aromatherapy Cause Candle by Lasting Smiles"
                      className="rounded"
                      src={require("../public/images/products/sea-salt-citrus-aromatherapy-cause-candle/box.jpg") as string}
                      style={{ maxWidth: 200 }}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    />
                  </picture>
                  <br />
                  <span className="border-b-2 border-black font-medium inline-block uppercase">
                    Sea Salt &amp; Citrus
                  </span>
                </a>
              </Link>
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default RelatedProducts;
